import React from "react";
import {
  sponsorTiers,
  sponsorshipTiers,
  bankingDetails,
  sponsorshipdetails,
} from "../data/mockData";
import { Award, Star, Medal, Trophy, Briefcase, Building2 } from "lucide-react";

const Sponsorship = () => {
  const getIcon = () => {
    return <Award size={48} />;
  };

  const getTierColor = (title) => {
    if (title.includes("Platinum"))
      return "border-blue-500 from-blue-50 to-blue-100";
    if (title.includes("Gold"))
      return "border-blue-500 from-blue-50 to-blue-100";
    if (title.includes("Silver"))
      return "border-blue-500 from-blue-50 to-blue-100";
    if (title.includes("Bronze"))
      return "border-blue-500 from-blue-50 to-blue-100";
    if (title.includes("Wellwisher"))
      return "border-blue-500 from-blue-50 to-blue-100";
    return "border-blue-500 from-blue-50 to-blue-100";
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Page Header */}
      <div className="bg-blue-950 py-16 mb-4 text-center">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 tracking-wider">
          SPONSORSHIP
        </h1>
        <div className="w-32 sm:w-48 h-1 bg-blue-600 mx-auto"></div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="mb-10 text-center text-4xl sm:text-5xl font-extrabold tracking-tight text-blue-950">
          Our Sponsors
        </h2>

        <div className="space-y-8">
          {sponsorTiers.map((tier) => (
            <div
              key={tier.name}
              className="rounded-2xl bg-gradient-to-r from-blue-200 via-blue-100 to-blue-200 px-4 py-6 sm:px-8 sm:py-8 shadow-sm"
            >
              <h3 className="mb-6 text-center text-2xl sm:text-3xl font-bold tracking-wide text-blue-950">
                {tier.name} Sponsors
              </h3>

              <div className="flex flex-wrap justify-center items-center gap-6">
                {tier.files.map((file) => (
                  <div
                    key={file}
                    className="w-40 h-28 sm:w-48 sm:h-32 p-4 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center justify-center"
                  >
                    <img
                      src={`/images/sponsors/${tier.folder || tier.name.toLowerCase()}/${file}`}
                      alt={`${tier.name} sponsor`}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-green-50 border-l-4 border-green-600 rounded-xl p-8 mb-4 text-center">
          <h3 className="text-3xl font-bold text-green-900 mb-4">
            Interested in Sponsorship?
          </h3>

          <p className="text-lg text-green-800 mb-6">
            Contact us for detailed sponsorship packages and custom partnership
            opportunities
          </p>

          <a
            href={sponsorshipdetails}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-lg inline-block"
          >
            View Sponsorship Details
          </a>
        </div>

        <div className="text-center mb-12">
          <p className="text-xl text-gray-700">
            Partner with us to make TG-SMART 2026 a grand success
          </p>
        </div>

        {/* Sponsorship Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {sponsorshipTiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-gradient-to-br ${getTierColor(tier.title)} rounded-xl p-8 text-center shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-t-4 ${getTierColor(tier.title).split(" ")[0]}`}
            >
              <div className="text-primary-700 mb-4 flex justify-center">
                {getIcon(tier.title)}
              </div>
              <h3 className="text-2xl font-bold text-primary-900 mb-4">
                {tier.title}
              </h3>
              <p className="text-3xl font-extrabold text-blue-950 mb-6">
                {tier.amount}
              </p>
              <button className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-lg w-full">
                Become a Sponsor
              </button>
            </div>
          ))}
        </div>

        {/* Banking Details */}
        <div className="bg-white rounded-xl shadow-xl p-8 mb-8 border-t-4 border-blue-600">
          <div className="flex items-center gap-3 mb-6">
            <Building2 size={32} className="text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-800">
              Banking Details for Payment
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gradient-to-r from-blue-100 to-blue-200 p-6 rounded-lg">
            <div>
              <p className="text-sm text-gray-600 font-semibold">
                Account Number
              </p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.accountNumber}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">
                Account Holder's Name
              </p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.holderName}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">Bank Name</p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.bankName}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">Branch</p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.branch}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">IFSC Code</p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.ifscCode}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">MICR Code</p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.MICRcode}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold">SWIFT Code</p>
              <p className="text-lg text-gray-800 font-bold">
                {bankingDetails.swiftCode}
              </p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-br from-primary-100 to-blue-200 rounded-xl p-8 mb-12 border-l-4 border-primary-600">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">
            Sponsorship Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Brand Visibility",
                desc: "Logo placement on conference materials, website, and banners",
              },
              {
                title: "Networking Opportunities",
                desc: "Access to delegates, researchers, and industry professionals",
              },
              {
                title: "Exhibition Space",
                desc: "Dedicated space for product/service showcase",
              },
              {
                title: "Growth Opportunities",
                desc: "Platform to promote your products",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary-500"
              >
                <h4 className="text-xl font-bold text-primary-900 mb-2">
                  {benefit.title}
                </h4>
                <p className="text-gray-700">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sponsorship;
